import cn from "classnames";

export type BorderRadiusSize = "none" | "small" | "medium" | "large";
export type BorderRadiusCorners = "full" | "left" | "right" | "top" | "bottom";

/** Single-breakpoint radius. Prefer this when there are no device overrides. */
export function borderRadiusStyles(
  size: BorderRadiusSize | null | undefined,
  corners: BorderRadiusCorners | null | undefined = "full",
) {
  if (!size || size === "none") return undefined;

  const corner = corners ?? "full";
  const classes = SINGLE_BREAKPOINT[size]?.[corner];
  if (!classes) return undefined;

  return cn("overflow-hidden", classes);
}

/**
 * Responsive radius following Container breakpoints: mobile (base), tablet
 * (md:), desktop (lg: = Default). Omit tablet/mobile to inherit Default.
 */
export function responsiveBorderRadiusStyles({
  size,
  corners,
  tabletSize,
  tabletCorners,
  mobileSize,
  mobileCorners,
}: {
  size: BorderRadiusSize | null | undefined;
  corners?: BorderRadiusCorners | null;
  tabletSize?: BorderRadiusSize | null;
  tabletCorners?: BorderRadiusCorners | null;
  mobileSize?: BorderRadiusSize | null;
  mobileCorners?: BorderRadiusCorners | null;
}) {
  const hasOverrides = Boolean(
    tabletSize || tabletCorners || mobileSize || mobileCorners,
  );

  if (!hasOverrides) {
    return borderRadiusStyles(size, corners);
  }

  const desktopSize = size ?? "none";
  const desktopCorners = corners ?? "full";
  const resolvedTabletSize = tabletSize ?? desktopSize;
  const resolvedTabletCorners = tabletCorners ?? desktopCorners;
  const resolvedMobileSize = mobileSize ?? desktopSize;
  const resolvedMobileCorners = mobileCorners ?? desktopCorners;

  const anyRounded =
    resolvedMobileSize !== "none" ||
    resolvedTabletSize !== "none" ||
    desktopSize !== "none";

  return cn(
    anyRounded && "overflow-hidden",
    radiusAtBreakpoint("base", resolvedMobileSize, resolvedMobileCorners),
    radiusAtBreakpoint("tablet", resolvedTabletSize, resolvedTabletCorners),
    radiusAtBreakpoint("desktop", desktopSize, desktopCorners),
  );
}

function radiusAtBreakpoint(
  breakpoint: "base" | "tablet" | "desktop",
  size: BorderRadiusSize,
  corners: BorderRadiusCorners,
) {
  if (size === "none") return RESPONSIVE[breakpoint].none;
  return RESPONSIVE[breakpoint][size][corners];
}

const SINGLE_BREAKPOINT = {
  small: {
    full: "rounded-lg",
    left: "rounded-l-lg",
    right: "rounded-r-lg",
    top: "rounded-t-lg",
    bottom: "rounded-b-lg",
  },
  medium: {
    full: "rounded-xl",
    left: "rounded-l-xl",
    right: "rounded-r-xl",
    top: "rounded-t-xl",
    bottom: "rounded-b-xl",
  },
  large: {
    full: "rounded-2xl",
    left: "rounded-l-2xl",
    right: "rounded-r-2xl",
    top: "rounded-t-2xl",
    bottom: "rounded-b-2xl",
  },
} as const;

/** Explicit per-corner classes so breakpoint overrides can reset unused corners. */
const RESPONSIVE = {
  base: {
    none: "rounded-tl-none rounded-tr-none rounded-bl-none rounded-br-none",
    small: {
      full: "rounded-tl-lg rounded-tr-lg rounded-bl-lg rounded-br-lg",
      left: "rounded-tl-lg rounded-tr-none rounded-bl-lg rounded-br-none",
      right: "rounded-tl-none rounded-tr-lg rounded-bl-none rounded-br-lg",
      top: "rounded-tl-lg rounded-tr-lg rounded-bl-none rounded-br-none",
      bottom: "rounded-tl-none rounded-tr-none rounded-bl-lg rounded-br-lg",
    },
    medium: {
      full: "rounded-tl-xl rounded-tr-xl rounded-bl-xl rounded-br-xl",
      left: "rounded-tl-xl rounded-tr-none rounded-bl-xl rounded-br-none",
      right: "rounded-tl-none rounded-tr-xl rounded-bl-none rounded-br-xl",
      top: "rounded-tl-xl rounded-tr-xl rounded-bl-none rounded-br-none",
      bottom: "rounded-tl-none rounded-tr-none rounded-bl-xl rounded-br-xl",
    },
    large: {
      full: "rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl rounded-br-2xl",
      left: "rounded-tl-2xl rounded-tr-none rounded-bl-2xl rounded-br-none",
      right: "rounded-tl-none rounded-tr-2xl rounded-bl-none rounded-br-2xl",
      top: "rounded-tl-2xl rounded-tr-2xl rounded-bl-none rounded-br-none",
      bottom: "rounded-tl-none rounded-tr-none rounded-bl-2xl rounded-br-2xl",
    },
  },
  tablet: {
    none: "md:rounded-tl-none md:rounded-tr-none md:rounded-bl-none md:rounded-br-none",
    small: {
      full: "md:rounded-tl-lg md:rounded-tr-lg md:rounded-bl-lg md:rounded-br-lg",
      left: "md:rounded-tl-lg md:rounded-tr-none md:rounded-bl-lg md:rounded-br-none",
      right:
        "md:rounded-tl-none md:rounded-tr-lg md:rounded-bl-none md:rounded-br-lg",
      top: "md:rounded-tl-lg md:rounded-tr-lg md:rounded-bl-none md:rounded-br-none",
      bottom:
        "md:rounded-tl-none md:rounded-tr-none md:rounded-bl-lg md:rounded-br-lg",
    },
    medium: {
      full: "md:rounded-tl-xl md:rounded-tr-xl md:rounded-bl-xl md:rounded-br-xl",
      left: "md:rounded-tl-xl md:rounded-tr-none md:rounded-bl-xl md:rounded-br-none",
      right:
        "md:rounded-tl-none md:rounded-tr-xl md:rounded-bl-none md:rounded-br-xl",
      top: "md:rounded-tl-xl md:rounded-tr-xl md:rounded-bl-none md:rounded-br-none",
      bottom:
        "md:rounded-tl-none md:rounded-tr-none md:rounded-bl-xl md:rounded-br-xl",
    },
    large: {
      full: "md:rounded-tl-2xl md:rounded-tr-2xl md:rounded-bl-2xl md:rounded-br-2xl",
      left: "md:rounded-tl-2xl md:rounded-tr-none md:rounded-bl-2xl md:rounded-br-none",
      right:
        "md:rounded-tl-none md:rounded-tr-2xl md:rounded-bl-none md:rounded-br-2xl",
      top: "md:rounded-tl-2xl md:rounded-tr-2xl md:rounded-bl-none md:rounded-br-none",
      bottom:
        "md:rounded-tl-none md:rounded-tr-none md:rounded-bl-2xl md:rounded-br-2xl",
    },
  },
  desktop: {
    none: "lg:rounded-tl-none lg:rounded-tr-none lg:rounded-bl-none lg:rounded-br-none",
    small: {
      full: "lg:rounded-tl-lg lg:rounded-tr-lg lg:rounded-bl-lg lg:rounded-br-lg",
      left: "lg:rounded-tl-lg lg:rounded-tr-none lg:rounded-bl-lg lg:rounded-br-none",
      right:
        "lg:rounded-tl-none lg:rounded-tr-lg lg:rounded-bl-none lg:rounded-br-lg",
      top: "lg:rounded-tl-lg lg:rounded-tr-lg lg:rounded-bl-none lg:rounded-br-none",
      bottom:
        "lg:rounded-tl-none lg:rounded-tr-none lg:rounded-bl-lg lg:rounded-br-lg",
    },
    medium: {
      full: "lg:rounded-tl-xl lg:rounded-tr-xl lg:rounded-bl-xl lg:rounded-br-xl",
      left: "lg:rounded-tl-xl lg:rounded-tr-none lg:rounded-bl-xl lg:rounded-br-none",
      right:
        "lg:rounded-tl-none lg:rounded-tr-xl lg:rounded-bl-none lg:rounded-br-xl",
      top: "lg:rounded-tl-xl lg:rounded-tr-xl lg:rounded-bl-none lg:rounded-br-none",
      bottom:
        "lg:rounded-tl-none lg:rounded-tr-none lg:rounded-bl-xl lg:rounded-br-xl",
    },
    large: {
      full: "lg:rounded-tl-2xl lg:rounded-tr-2xl lg:rounded-bl-2xl lg:rounded-br-2xl",
      left: "lg:rounded-tl-2xl lg:rounded-tr-none lg:rounded-bl-2xl lg:rounded-br-none",
      right:
        "lg:rounded-tl-none lg:rounded-tr-2xl lg:rounded-bl-none lg:rounded-br-2xl",
      top: "lg:rounded-tl-2xl lg:rounded-tr-2xl lg:rounded-bl-none lg:rounded-br-none",
      bottom:
        "lg:rounded-tl-none lg:rounded-tr-none lg:rounded-bl-2xl lg:rounded-br-2xl",
    },
  },
} as const;
